import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import type { Legend } from '@/features/legends/Legend'
import {
  getLegendStatus,
  hasUnpublishedChanges,
  type LegendStatus,
} from '@/features/legends/legend-status'
import type { LegendStore } from '@/features/legends/LegendStore'
import type { LegendVersion } from '@/features/legends/LegendVersion'
import { formatRelativeTime } from '@/features/legends/relative-time'
import type { NavigateFn } from '@/RootStore'
import { links } from '@/utils/link'

/**
 * A legend in the legend list.
 */
export class LegendListItemViewModel {
  constructor(
    readonly legend: Legend,
    private readonly _legendStore: LegendStore,
    private readonly _canModify: () => boolean,
    private readonly _onActivationToggled: () => void,
    private readonly _now: () => Date,
    private readonly _navigate: NavigateFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * The latest version, which the list shows.
   */
  @computed
  get latestVersion(): LegendVersion {
    // SAFETY: the list only has legends whose latest version is loaded.
    return this._legendStore.latestVersionFor(this.legend.id)!
  }

  @computed
  get status() {
    return getLegendStatus(this.legend.inactive, this.latestVersion)
  }

  @computed
  get hasUnpublishedChanges() {
    return hasUnpublishedChanges(this.latestVersion)
  }

  /**
   * The status of the latest version, for its version chip.
   */
  @computed
  get versionStatus(): LegendStatus {
    if (this.legend.inactive) {
      return 'Inactive'
    }
    return this.latestVersion.published ? 'Published' : 'Draft'
  }

  /**
   * When the latest version was published, or last changed.
   */
  @computed
  get dateLabel() {
    const { published, datePublished, dateModified } = this.latestVersion
    return published && datePublished
      ? `Published ${formatRelativeTime(datePublished, this._now())}`
      : `Last changed ${formatRelativeTime(dateModified, this._now())}`
  }

  /**
   * Whether the user may (de)activate the legend.
   */
  @computed
  get canModify() {
    return this._canModify()
  }

  /**
   * Opens the legend in the editor.
   */
  @action.bound
  edit() {
    this._navigate(links.legend(this.legend.id), { viewTransition: true })
  }

  /**
   * Activates the legend when inactive, deactivates it otherwise. A failure is
   * reported by the alert.
   */
  toggleActivation = task.resolved(() =>
    this._toggleActivation().catch(() => undefined),
  )

  private async _toggleActivation() {
    const legend = this.legend
    await (
      legend.inactive
        ? this._legendStore.activateLegend(legend)
        : this._legendStore.deactivateLegend(legend)
    ).catch(
      this._createAlertErrorHandler(
        legend.inactive
          ? 'Activating the legend failed'
          : 'Deactivating the legend failed',
      ),
    )
    this._onActivationToggled()
  }
}
